import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { Box, Button, Stack, Typography } from "@mui/material";
import type { ElementType, ReactNode } from "react";

import { getYoutubeEmbedUrl } from "@/shared/utils/youtube/youtubeEmbedUrl";
import { HOPE_SANS, HOPE_SERIF } from "@/shared/branding/thehopeonebrand";

import type { ImpactFundRecipient } from "../utils/impactCardData";
import type { AnchorCube } from "../utils/anchorCubes";
import { columnImageUrls } from "../utils/columnExperience";
import { ImpactStoryVideo } from "./ImpactStoryGallery";
import pmReliefImage1 from "../assets/PM_Fund_Nepal_1.jpeg";


const c = {
  forestDeep: "#102C24",
  forest: "#1D4437",
  green: "#2F6B4F",
  mist: "#EEF5EF",
  paper: "#FFFFFF",
  muted: "#6B7A73",
  ink: "#1B2430",
  border: "rgba(16,44,36,.10)",
};

function instagramPermalink(url?: string) {
  if (!url) return "";
  const trimmed = url.trim();
  if (!/instagram\.com/i.test(trimmed)) return "";
  return trimmed.split("?")[0].replace(/\/$/, "");
}

export function ImpactMediaEmbed({ url, title }: { url?: string; title?: string }) {
  const instagram = instagramPermalink(url);
  const youtube = url ? getYoutubeEmbedUrl(url) : null;
  if (instagram && /\/(p|reel|tv)\//i.test(instagram)) {
    return (
      <Box
        sx={{
          position: "relative",
          borderRadius: { xs: "12px", sm: "16px" },
          overflow: "hidden",
          bgcolor: "#102C24",
          aspectRatio: "4 / 5",
          width: "100%",
        }}
      >
        <Box
          component="iframe"
          title={title || "Instagram"}
          src={`${instagram}/embed`}
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
        />
      </Box>
    );
  }
  if (youtube) {
    /** Shorts play vertically, so they get a portrait frame. */
    const vertical = /\/shorts\//i.test(url ?? "");
    return (
      <Box
        sx={{
          position: "relative",
          borderRadius: { xs: "12px", sm: "16px" },
          overflow: "hidden",
          bgcolor: "#102C24",
          aspectRatio: vertical ? "9 / 16" : "16 / 9",
          width: "100%",
          maxWidth: vertical ? { xs: 260, sm: 320 } : "none",
          mx: vertical ? "auto" : 0,
        }}
      >
        <Box
          component="iframe"
          title={title || "Video"}
          src={youtube}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
        />
      </Box>
    );
  }
  if (url && !instagram) return <ImpactStoryVideo src={url} />;
  return null;
}

/** A single static image (local asset or remote URL) rendered as a proper <img>,
 *  matching the rounded, cropped frame used elsewhere on this page. */
function ImpactStaticImage({
  src,
  alt,
  aspectRatio = "4 / 3",
}: {
  src: string;
  alt: string;
  aspectRatio?: string;
}) {
  if (!src) return null;
  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: { xs: "12px", sm: "16px" },
        overflow: "hidden",
        bgcolor: c.mist,
        aspectRatio,
        width: "100%",
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
          display: "block",
        }}
      />
    </Box>
  );
}

const FOCUS_ICONS: ElementType[] = [
  Inventory2OutlinedIcon,
  HomeOutlinedIcon,
  FavoriteBorderRoundedIcon,
  LocalHospitalOutlinedIcon,
];

/** One numbered section of the recipient introduction. */
function IntroBlock({
  index,
  label,
  body,
  accent = false,
  children,
}: {
  index: string;
  label: string;
  body?: string;
  accent?: boolean;
  children?: ReactNode;
}) {
  if (!body && !children) return null;
  return (
    <Box
      sx={{
        p: { xs: 1.4, sm: 1.9 },
        borderRadius: { xs: "16px", sm: "20px" },
        bgcolor: accent ? c.mist : c.paper,
        border: `1px solid ${accent ? "rgba(47,107,79,.22)" : c.border}`,
      }}
    >
      <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mb: { xs: 0.7, sm: 0.9 } }}>
        <Box
          sx={{
            minWidth: { xs: 24, sm: 26 },
            height: { xs: 24, sm: 26 },
            px: 0.6,
            borderRadius: "999px",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            bgcolor: accent ? c.green : c.mist,
            color: accent ? "#FFFFFF" : c.green,
            fontFamily: HOPE_SANS,
            fontWeight: 800,
            fontSize: { xs: "0.62rem", sm: "0.66rem" },
            letterSpacing: "0.04em",
          }}
        >
          {index}
        </Box>
        <Typography
          sx={{
            fontFamily: HOPE_SANS,
            fontSize: { xs: "0.6rem", sm: "0.64rem" },
            fontWeight: 800,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: c.green,
          }}
        >
          {label}
        </Typography>
      </Stack>

      {body ? (
        <Typography
          sx={{
            fontFamily: HOPE_SANS,
            fontSize: { xs: "0.88rem", sm: "0.95rem" },
            lineHeight: { xs: 1.62, sm: 1.7 },
            color: accent ? c.forestDeep : c.muted,
          }}
        >
          {body}
        </Typography>
      ) : null}

      {children}
    </Box>
  );
}

type ImpactRecipientPageProps = {
  recipient?: ImpactFundRecipient;
  cube?: AnchorCube;
  fallbackTitle: string;
  fallbackRole: string;
  variant: "pm" | "aol";
};

export default function ImpactRecipientPage({
  recipient,
  cube,
  fallbackTitle,
  fallbackRole,
  variant,
}: ImpactRecipientPageProps) {
  const title = recipient?.name || cube?.title || fallbackTitle;
  const tagline = recipient?.tagline || recipient?.role || fallbackRole;
  const description = recipient?.description || cube?.description;
  const website = recipient?.websiteUrl;
  const instagram = recipient?.instagramUrl;

  // Fall back to a bundled local image when neither the recipient nor the
  // cube supplies one — this is what keeps the "pm" variant's hero from
  // rendering blank.
  const hero =
    recipient?.heroImageUrl || columnImageUrls(cube?.experience)[0] || (variant === "pm" ? pmReliefImage1 : undefined);

  const focusAreas = recipient?.focusAreas ?? [];

  /* This page is the written introduction. Their photographs and videos are
     the subpages stacked underneath it. */
  const about = recipient?.about || description;
  const whatTheyDo = recipient?.whatTheyDo;
  const whatFundsWillDo = recipient?.whatFundsWillDo;
  const hasWorkSubpages = Boolean(cube?.columns?.length);

  // Static bundled gallery images shown only for the PM Relief Fund variant,
  // in addition to (not instead of) whatever dynamic media the cube provides.
  // const staticGalleryImages =
  //   variant === "pm" ? [pmReliefImage1].filter(Boolean) : [];

  return (
    <Stack spacing={{ xs: 1.4, sm: 1.75 }}>
      {hero ? (
        <Box
          sx={{
            position: "relative",
            mx: { xs: -1.75, sm: -3 },
            mt: { xs: -2, sm: -3.25 },
            height: { xs: 150, sm: 230 },
          }}
        >
          <Box
            component="img"
            src={hero}
            alt={title}
            loading="eager"
            decoding="async"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(16,44,36,0) 45%, rgba(16,44,36,.55) 100%)",
            }}
          />
        </Box>
      ) : null}

      <Box>
        <Typography
          sx={{
            fontFamily: HOPE_SANS,
            fontSize: { xs: "0.6rem", sm: "0.64rem" },
            fontWeight: 800,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: c.green,
          }}
        >
          기금 지원 기관
        </Typography>
        <Typography
          sx={{
            mt: 0.5,
            fontFamily: HOPE_SANS,
            fontWeight: 800,
            fontSize: { xs: "1.4rem", sm: "1.7rem", md: "2rem" },
            lineHeight: 1.14,
            letterSpacing: "-0.03em",
            color: c.ink,
            wordBreak: "keep-all",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            mt: 0.55,
            fontFamily: HOPE_SERIF,
            fontStyle: "italic",
            fontSize: { xs: "0.9rem", sm: "1.02rem" },
            color: c.forest,
          }}
        >
          {tagline}
        </Typography>
      </Box>

      <Stack spacing={{ xs: 0.9, sm: 1.1 }}>
        <IntroBlock index="01" label="기관 소개" body={about} />
        <IntroBlock index="02" label="주요 활동" body={whatTheyDo}>
          {focusAreas.length ? (
            <Box
              sx={{
                mt: whatTheyDo ? { xs: 1, sm: 1.25 } : 0,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: { xs: 0.7, sm: 0.85 },
              }}
            >
              {focusAreas.map((area, index) => {
                const Icon = FOCUS_ICONS[index % FOCUS_ICONS.length];
                return (
                  <Stack key={area} direction="row" spacing={0.9} alignItems="center">
                    <Icon sx={{ fontSize: { xs: 16, sm: 18 }, color: c.green, flexShrink: 0 }} />
                    <Typography
                      sx={{ fontFamily: HOPE_SANS, fontSize: { xs: "0.8rem", sm: "0.86rem" }, color: c.ink }}
                    >
                      {area}
                    </Typography>
                  </Stack>
                );
              })}
            </Box>
          ) : null}
        </IntroBlock>
        <IntroBlock
          index="03"
          label="후원금이 만드는 변화"
          body={whatFundsWillDo}
          accent
        />
      </Stack>

      {/* {staticGalleryImages.length ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: { xs: 0.85, sm: 1.1 },
          }}
        >
          {staticGalleryImages.map((src, i) => (
            <ImpactStaticImage key={src} src={src} alt={`${title} 현장 사진 ${i + 1}`} />
          ))}
        </Box>
      ) : null} */}

      {website || instagram ? (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          justifyContent="center"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          {/* {website ? (
            <Button
              href={website}
              target="_blank"
              rel="noreferrer"
              startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none",
                fontFamily: HOPE_SANS,
                fontWeight: 700,
                fontSize: { xs: "0.82rem", sm: "0.85rem" },
                color: c.forest,
                borderRadius: "999px",
                border: `1px solid ${c.border}`,
                px: 2,
                py: { xs: 0.9, sm: 0.75 },
              }}
            >
              {variant === "aol" ? "공식 페이지 방문" : "공식 웹사이트 방문"}
            </Button>
          ) : null} */}
          {instagram && instagram !== website ? (
            <Button
              href={instagram}
              target="_blank"
              rel="noreferrer"
              startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none",
                fontFamily: HOPE_SANS,
                fontWeight: 700,
                fontSize: { xs: "0.82rem", sm: "0.85rem" },
                color: c.forest,
                borderRadius: "999px",
                border: `1px solid ${c.border}`,
                px: 2,
                py: { xs: 0.9, sm: 0.75 },
              }}
            >
              인스타그램
            </Button>
          ) : null}
        </Stack>
      ) : null}
{/* 
      {hasWorkSubpages ? (
        <Stack direction="row" spacing={0.7} alignItems="center" justifyContent="center" sx={{ px: { xs: 1, sm: 0 }, textAlign: "center" }}>
          <ArrowDownwardRoundedIcon sx={{ fontSize: 16, color: c.green, flexShrink: 0 }} />
          <Typography
            sx={{
              fontFamily: HOPE_SANS,
              fontSize: { xs: "0.7rem", sm: "0.76rem" },
              fontWeight: 700,
              letterSpacing: "0.06em",
              color: c.muted,
            }}
          >
            아래로 스크롤하여 사진과 영상으로 활동을 확인해 보세요.
          </Typography>
        </Stack>
      ) : null} */}
    </Stack>
  );
}